import { Component, ElementRef, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AppModule } from 'src/app/_helpers/module';
import { ConfigService } from 'src/app/_services/config.service';
import { HeaderService } from 'src/app/_services/header.service';
import { DictionaryService } from '../../dictionary/dictionary.service';
import { customValidators } from '../../_helpers/customValidators';
import { globalHelper } from '../../_helpers/global';
import { Utils } from '../../_helpers/utils';
import { User } from '../../_models/user.model';
import { AuthenticationService, UserService } from '../../_services';
import { TranslateConfigService } from '../../_services/translate-config.service';
import { BuildingService } from '../building/building.service';
import { ModalSelectDropdownComponent } from '../_component/modal-select-dropdown/modal-select-dropdown.component';
import { ModalSelectDropdownSettings } from '../_component/modal-select-dropdown/modal-select-dropdown.model';
import { ThisPlatform } from './../../_services/platform.service';
import { ChangePasswordComponent } from './change-password/change-password.component';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.page.html',
  styleUrls: [
    './../../_styles/ion-grid-box.scss',
    './../../_styles/ion-grid-content-table.scss',
    './settings.page.scss',
  ],
})
export class SettingsPage implements OnInit {
  userStorage: User;
  user: User;
  compareWith: any;
  form: FormGroup;
  validationError = false;
  disableSaveButton = false;
  deleteAvatar = false;
  fileData: File = null;
  currentLanguage;

  avatar: File;

  languageReloader: boolean = true;
  appModule = AppModule;

  constructor(
    public userServ: UserService,
    public translateConfigService: TranslateConfigService,
    public configService: ConfigService,
    public buildingServ: BuildingService,
    private dictionaryServ: DictionaryService,
    public toastController: ToastController,
    public translateService: TranslateService,
    private el: ElementRef,
    private authService: AuthenticationService,
    public modalController: ModalController,
    public p: ThisPlatform,
    private headerServ: HeaderService,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController
  ) {}

  ngOnInit() {
    this.userStorage = new User();
    this.user = new User();
    this.initForm();

    this.userStorage = JSON.parse(localStorage.getItem('currentUser'));

    if (this.userStorage !== null) {
      this.userStorage.language = this.translateConfigService.setLanguage(
        this.userStorage.language
      );
      this.currentLanguage = this.translateConfigService
        .getLanguageList()
        .find((lang) => {
          return lang.value == this.userStorage.language;
        });
    }
  }

  initFormData() {
    this.form.patchValue({
      email: this.user.email,
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      telephone: this.user.telephone,
      mobilePhone: this.user.mobilePhone,
      carPlate: this.user.carPlate,
      language: this.user.language,
      building: this.buildingServ.findBuilding(this.user.idBuilding),
      pushAccepted: this.user.pushAccepted,
      itemImage: this.user.avatar,
    });
  }

  initForm() {
    this.form = new FormGroup({
      email: new FormControl(null, {
        updateOn: 'blur',
      }),
      firstName: new FormControl(null, {
        updateOn: 'blur',
        validators: [Validators.required, Validators.maxLength(50)],
      }),
      lastName: new FormControl(null, {
        updateOn: 'blur',
        validators: [Validators.required, Validators.maxLength(50)],
      }),
      telephone: new FormControl(null, {
        updateOn: 'blur',
        validators: [
          customValidators.phoneNumberPattern,
          Validators.maxLength(15),
          customValidators.maxLengthWithoutWhiteSpacesValidator(15),
          Validators.minLength(9),
        ],
      }),
      mobilePhone: new FormControl(null, {
        updateOn: 'blur',
        validators: [
          customValidators.phoneNumberPattern,
          Validators.maxLength(15),
          customValidators.maxLengthWithoutWhiteSpacesValidator(15),
          Validators.minLength(9),
        ],
      }),
      carPlate: new FormControl(null, {
        updateOn: 'blur',
        validators: [Validators.maxLength(15)],
      }),
      language: new FormControl(null, {
        updateOn: 'blur',
        validators: [],
      }),
      building: new FormControl(null, {
        updateOn: 'blur',
        validators: [Validators.required],
      }),
      pushAccepted: new FormControl('', {
        updateOn: 'blur',
      }),
      itemImage: new FormControl(null, {
        updateOn: 'blur',
        validators: [
          customValidators.fileMaxSize(10000000),
          customValidators.fileFormat([
            'jpg',
            'png',
            'gif',
            'jfif',
            'pjpeg',
            'jpeg',
            'pjp',
          ]),
        ],
      }),
    });
  }

  async ionViewWillEnter() {
    await this.buildingServ.loadBuildingsSync();

    this.userServ.getById(this.userStorage.id).subscribe((user: User) => {
      this.user = user;
      this.initFormData();

      if (!this.user.idBuilding) {
        this.user.idBuilding = this.buildingServ.buildings[0].id;
      }
      this.buildingServ.buildings = this.buildingServ.buildings;

      if (this.user.language === null) {
        this.user.language = this.translateConfigService.getDefaultLanguage();
      } else {
        this.translateConfigService.setLanguage(this.user.language);
      }
    });

    this.compareWith = Utils.compareWithFn;
    this.headerServ.setHeaderInit({
      title: 'SETTINGS.SETTING',
    });
  }

  buildingChange(value) {
    this.user.idBuilding = Number(value);
  }

  async save() {
    this.disableSaveButton = true;

    if (!this.form.valid) {
      this.validationError = true;
      this.disableSaveButton = false;
      globalHelper.scrollToFirstInvalidControl(this.el);
      return;
    }

    const {
      building,
      telephone,
      mobilePhone,
      carPlate,
      pushAccepted,
    } = this.form.value;

    const formData = new FormData();

    building ? formData.append('idBuilding', building.id) : null;
    telephone
      ? formData.append('telephone', telephone.replace(/\s/g, ''))
      : null;
    mobilePhone
      ? formData.append('mobilePhone', mobilePhone.replace(/\s/g, ''))
      : null;
    carPlate ? formData.append('carPlate', carPlate) : null;
    formData.append('language', this.currentLanguage.value);
    formData.append('pushAccepted', pushAccepted);
    if (!this.deleteAvatar) {
      formData.append('file', this.fileData);
    } else {
      formData.append('deleteAvatar', 'true');
    }

    //do usuniecia po aktualziacji api
    this.user.id = this.userStorage.id;
    building ? (this.user.idBuilding = building.id) : null;

    this.userServ
      .update(this.userStorage.id, formData)
      .subscribe(async (res: User) => {
        this.user = res;

        this.dictionaryServ.refreshDictionary();
        this.disableSaveButton = false;
        this.userStorage = {
          ...this.userStorage,
          ...this.user,
        };
        localStorage.setItem('currentUser', JSON.stringify(this.userStorage));
        this.authService.currentUserSubject.next(this.userStorage);
        await this.translateConfigService.setLanguage(this.user.language);
        this.languageReloader = false;
        setTimeout(async () => {
          this.translateConfigService.getLanguageList().forEach((l) => {
            if (l.value === this.currentLanguage.value) {
              this.currentLanguage = l;
            }
          });
          this.languageReloader = true;
          const toast = await this.toastController.create({
            message: this.translateService.instant('SAVED'),
            duration: 1000,
            color: 'dark',
          });
          toast.present();
        }, 0);
      });
  }

  imageUploaded(file: File) {
    this.deleteAvatar = false;
    this.fileData = file;
  }

  imageRemoved($event) {
    this.deleteAvatar = true;
    this.fileData = null;
  }

  async changeLanguage() {
    let modalSettings: ModalSelectDropdownSettings = {
      data: this.translateConfigService.getLanguageList(),
      currentValue: this.currentLanguage,
      header: 'SETTINGS.LANGUAGE',
      valueRequied: true,
    };

    const modal = await this.modalController.create({
      component: ModalSelectDropdownComponent,
      componentProps: {
        modalSettings: modalSettings,
      },
      cssClass: 'add-edit-modal add-edit-modal--small',
    });
    await modal.present();
    const data = (await modal.onDidDismiss()).data;
    if (data) {
      this.currentLanguage = data;
    }
  }

  async onChangePasswordButtonClick() {
    const modal = await this.modalController.create({
      component: ChangePasswordComponent,
      cssClass: 'change-password-modal',
    });
    await modal.present();
  }

  async onDeleteAccount() {
    let confirm = await globalHelper.Alert(
      this.alertCtrl,
      this.translateService,
      'SETTINGS.DELETE_ACCOUNT_CONFIRM',
      'YES',
      'NO'
    );
    if (confirm) {
      this.loadingCtrl
        .create({
          keyboardClose: true,
          message: this.translateService.instant('DELETING'),
        })
        .then((loadingEl) => {
          loadingEl.present();
          this.userServ.delete(this.user.id).subscribe((e) => {
            loadingEl.dismiss();
            this.authService.logout();
          });
        });
    }
  }
}
